/**
 * CourseCurriculumTab
 *
 * Curriculum management for the course editor (Tab 3).
 *
 * Three modes based on course type:
 *  - Standard course → AdminCurriculumBuilder (module cards + lesson rows
 *    with type icons + inline add-lesson)
 *  - Learning Path   → PathCourseEditor (constituent courses)
 *  - External event  → notice (no curriculum applies)
 *
 * "Enforce linear progress" lives at the top of Standard curriculum as a
 * compact inline strip — it's a curriculum-flow setting, not a module/
 * lesson concern, but it doesn't deserve a whole card.
 *
 * No explicit Save button — every CRUD action commits via the hooks.
 */

import { useEffect, useState } from 'react';
import { Course, CourseType } from '../../apis/api-client';
import EditLessonDialog from '../EditLesson';
import EditModuleDialog from '../EditModule';
import ConfirmDialog from '../ConfirmDialog';
import PathCourseEditor from './PathCourseEditor';
import AdminCurriculumBuilder from './AdminCurriculumBuilder';
import LessonPreview from './LessonPreview';
import { useLessonManagement } from '../../hooks/useLessonManagement';
import { useModuleManagement } from '../../hooks/useModuleManagement';
import { Box, FormControlLabel, Switch, Typography } from '../ui';
import { coursesClient } from '../../apis/client-instances';
import { getDisplayModuleNames } from '../../utils/coursePrefs';
import { courseToSettings } from '../../utils/courseSettings';

export interface CourseCurriculumTabProps {
  course: Course | null;
  onCourseChanged?: () => void;
}

export default function CourseCurriculumTab({ course, onCourseChanged }: CourseCurriculumTabProps) {
  const courseId = course?.id ?? 0;
  const lessonManagement = useLessonManagement(courseId);
  const moduleManagement = useModuleManagement(courseId);

  // Tracks which lesson is showing in the right-pane preview.
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
  // Recompute the selected lesson object from the latest fetched list so
  // edits update the preview immediately.
  const selectedLesson = selectedLessonId
    ? lessonManagement.lessons.find((l) => l.id === selectedLessonId) ?? null
    : null;
  // When the selected lesson gets deleted out from under us, drop the
  // selection rather than leaving a stale preview.
  useEffect(() => {
    if (selectedLessonId && !lessonManagement.lessons.find((l) => l.id === selectedLessonId)) {
      setSelectedLessonId(null);
    }
  }, [selectedLessonId, lessonManagement.lessons]);

  // Display module + lesson names — persisted on the course settings blob.
  // Defaults off so a fresh course presents as a flat lesson list; the
  // author opts in when modules carry real meaning for the learner.
  const [displayModuleNames, setDisplayModuleNamesState] = useState(false);
  useEffect(() => {
    setDisplayModuleNamesState(getDisplayModuleNames(course));
  }, [course]);
  const handleDisplayModuleNamesToggle = async (next: boolean) => {
    if (!course?.id) return;
    setDisplayModuleNamesState(next); // optimistic
    try {
      await coursesClient.updateCourse(course.id, {
        ...(course as any),
        settings: { ...courseToSettings(course), displayModuleNames: next },
      });
      onCourseChanged?.();
    } catch {
      setDisplayModuleNamesState(!next); // revert on failure
    }
  };

  // Force linear progress — course-level flag that locks every lesson until
  // the immediately-preceding one is complete. It's a top-level field on the
  // course (not part of the settings blob), so the save rebuilds `settings`
  // from the loaded course to avoid wiping what other tabs own.
  const [enforceLinearProgress, setEnforceLinearProgressState] = useState(false);
  useEffect(() => {
    setEnforceLinearProgressState(course?.enforceLinearProgress === true);
  }, [course]);
  const handleEnforceLinearProgressToggle = async (next: boolean) => {
    if (!course?.id) return;
    setEnforceLinearProgressState(next); // optimistic
    try {
      await coursesClient.updateCourse(course.id, {
        ...(course as any),
        enforceLinearProgress: next,
        settings: courseToSettings(course),
      });
      onCourseChanged?.();
    } catch {
      setEnforceLinearProgressState(!next); // revert on failure
    }
  };

  // Initial fetch when course id becomes available.
  useEffect(() => {
    if (!courseId) return;
    lessonManagement.fetchLessons();
    moduleManagement.fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  if (!course) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Save the course settings first to start building the curriculum.
        </Typography>
      </Box>
    );
  }

  if (course.courseType === CourseType.External) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
          External events don't have a curriculum
        </Typography>
        <Typography color="text.secondary">
          Learners are redirected to the event URL you configured in Settings.
        </Typography>
      </Box>
    );
  }

  if (course.courseType === CourseType.LearningPath) {
    const handlePathChange = async (next: any[]) => {
      try {
        await coursesClient.updateCourse(course.id!, {
          ...(course as any),
          pathCourses: next,
        });
        onCourseChanged?.();
      } catch {
        /* surfaced by the editor */
      }
    };
    return (
      <Box sx={{ p: 1 }}>
        <PathCourseEditor
          pathCourses={
            (course.pathCourses as any)?.map((pc: any) => ({
              courseId: pc.courseId,
              courseTitle: pc.courseTitle || '',
              courseThumbnail: pc.courseThumbnail,
              sequenceOrder: pc.sequenceOrder,
            })) ?? []
          }
          onChange={handlePathChange}
          currentCourseId={course.id}
        />
      </Box>
    );
  }

  // ── Standard course curriculum ──────────────────────────────────────
  const totalLessons = lessonManagement.lessons.length;
  const totalModules = moduleManagement.modules.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Curriculum-level settings — auto-saves on flip. "Force linear
          progress" is the course-wide strict-order lock; it coexists with the
          per-lesson gates (the lock toggle on each row), which cover the
          narrower "free-flow, but lock this checkpoint" case. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={displayModuleNames}
                onChange={(e) => handleDisplayModuleNamesToggle(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                Display module names
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            Show module headings in the learner's curriculum view. Turn off when modules just duplicate the lesson title.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enforceLinearProgress}
                onChange={(e) => handleEnforceLinearProgressToggle(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                Force linear progress
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            Lock every lesson until the one before it is complete, so learners move through the curriculum in order. Use per-lesson locks instead to gate just a checkpoint while leaving earlier lessons free-flow.
          </Typography>
        </Box>
      </Box>

      {/* Header — counts only. Primary actions ("Add lesson" inside each
          module, "Add module" below the list) live with the content. */}
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Curriculum
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalModules > 0 || totalLessons > 0
            ? `${totalModules} ${totalModules === 1 ? 'module' : 'modules'} · ${totalLessons} ${totalLessons === 1 ? 'lesson' : 'lessons'}`
            : 'Add a module to start building your curriculum.'}
        </Typography>
      </Box>

      {/* Two-column body: thin builder on the left, lesson preview on
          the right. The builder is fixed-width so the preview takes the
          remaining space — on narrow viewports they stack via the
          `auto-fit` fallback in the grid below. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '420px 1fr' },
          gap: 3,
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <AdminCurriculumBuilder
            modules={moduleManagement.modules}
            lessons={lessonManagement.lessons}
            selectedLessonId={selectedLessonId}
            onSelectLesson={(l) => setSelectedLessonId(l.id)}
            onAddModule={moduleManagement.openAddModuleDialog}
            onEditModule={moduleManagement.openEditModuleDialog}
            onDeleteModule={moduleManagement.confirmDeleteModule}
            onAddLessonForModule={lessonManagement.openAddDialogForModule}
            onEditLesson={lessonManagement.openEditDialog}
            onDeleteLesson={lessonManagement.confirmDelete}
            onToggleGate={(l) => lessonManagement.updateLessonRules(l.id, { isGated: !(l.isGated === true) })}
            onReorderModules={moduleManagement.reorderModules}
            onReorderLessons={lessonManagement.reorderLessons}
          />
        </Box>

        <Box sx={{ position: 'sticky', top: 16 }}>
          <LessonPreview
            lesson={selectedLesson}
            onEdit={lessonManagement.openEditDialog}
          />
        </Box>
      </Box>

      {/* Dialogs — reused from the existing CourseDetail flow. */}
      <EditLessonDialog
        open={lessonManagement.showDialog}
        onSave={lessonManagement.saveLesson}
        onClose={lessonManagement.closeDialog}
        lessonToEdit={lessonManagement.lessonToEdit}
        modules={moduleManagement.modules}
      />

      <EditModuleDialog
        open={moduleManagement.showModuleDialog}
        onSave={moduleManagement.saveModule}
        onClose={moduleManagement.closeModuleDialog}
        moduleToEdit={moduleManagement.moduleToEdit}
      />

      <ConfirmDialog
        open={lessonManagement.confirmOpen}
        title="Delete Lesson"
        content="Are you sure you want to delete this lesson?"
        onConfirm={lessonManagement.deleteLesson}
        onCancel={lessonManagement.cancelDelete}
      />

      <ConfirmDialog
        open={moduleManagement.confirmModuleOpen}
        title="Delete Module"
        content="Are you sure you want to delete this module? Lessons inside will not be deleted."
        onConfirm={moduleManagement.deleteModule}
        onCancel={moduleManagement.cancelDeleteModule}
      />
    </Box>
  );
}
